import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { Link, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ROLES } from '../../../constants/roles';
import { SITE_CARE_MODES } from '../../../constants/siteCareModes';
import pharmacyService from '../../../services/pharmacyService';
import SiteService from '../../../services/siteService';
import Icon from '../../common/Icons';
import SiteCareModeBadge from '../../sites/SiteCareModeBadge';
import { CARE_MODE_OPTIONS } from '../../sites/siteListUtils';
import { buildSettingsStats, filterSettingsSites } from '../settings/settingsPageUtils';
import { ADMIN_PATHS } from '../adminPaths';
import { ADMIN_COPY } from '../adminCopy';
import { AdminPageHeader } from '../ui';
import '../CompanyManagement.css';
import '../../companies/CompanyListUx.css';
import '../../sites/SiteCareModeBadge.css';
import '../pages/AdminSitesPage.css';
import './AdminSettingsPage.css';

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const { toast } = useOutletContext() || { toast: () => {} };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [currentCentral, setCurrentCentral] = useState(null);
  const [selection, setSelection] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [siteDraft, setSiteDraft] = useState({
    site_care_mode: SITE_CARE_MODES.DOCTOR_NURSE_NO_PHARMACY,
    has_local_pharmacy: false
  });
  const [savingSite, setSavingSite] = useState(false);

  const canSetCentral = user?.role === ROLES.ADMIN;
  const selectedSite = useMemo(
    () => sites.find((s) => String(s.id) === String(selection)) || null,
    [sites, selection]
  );
  const expectedPhrase = selectedSite ? `CONFIRMER ${selectedSite.name}` : '';
  const stats = useMemo(() => buildSettingsStats(sites, currentCentral), [sites, currentCentral]);
  const filteredSites = useMemo(
    () => filterSettingsSites(sites, siteSearch),
    [sites, siteSearch]
  );
  const pharmacyLocked = siteDraft.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY;
  const centralUnchanged =
    selectedSite && String(selectedSite.id) === String(currentCentral?.id);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [sitesRes, centralRes] = await Promise.all([
        SiteService.getAllSites({ limit: 500, offset: 0, active_only: false }),
        pharmacyService.getCentralSite()
      ]);
      const allSites = sitesRes.success ? sitesRes.data || [] : [];
      const central = centralRes.success ? centralRes.data || null : null;
      setSites(allSites);
      setCurrentCentral(central);
      setSelection(central?.id ? String(central.id) : '');
    } catch (error) {
      toast('danger', error.message || 'Chargement des paramètres impossible.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const openConfirm = () => {
    if (!canSetCentral || !selectedSite || centralUnchanged) return;
    setConfirmInput('');
    setShowConfirm(true);
  };

  const applyChange = async () => {
    if (!canSetCentral || !selectedSite) return;
    setSaving(true);
    try {
      const res = await pharmacyService.setCentralSite(selectedSite.id);
      if (!res.success) {
        toast('danger', res.message || 'Mise à jour impossible.');
        return;
      }
      toast('success', `Pharmacie centrale : ${res.data?.name || selectedSite.name}.`);
      setShowConfirm(false);
      await loadSettings();
    } catch (error) {
      toast('danger', error.message || 'Mise à jour impossible.');
    } finally {
      setSaving(false);
    }
  };

  const openSiteEditor = (site) => {
    setEditingSite(site);
    setSiteDraft({
      site_care_mode: site.site_care_mode || SITE_CARE_MODES.DOCTOR_NURSE_NO_PHARMACY,
      has_local_pharmacy:
        site.has_local_pharmacy !== undefined
          ? Boolean(site.has_local_pharmacy)
          : site.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY
    });
  };

  const saveSiteCareSettings = async () => {
    if (!editingSite) return;
    setSavingSite(true);
    try {
      const payload = {
        site_care_mode: siteDraft.site_care_mode,
        has_local_pharmacy:
          siteDraft.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY
            ? true
            : Boolean(siteDraft.has_local_pharmacy)
      };
      const res = await SiteService.updateSite(editingSite.id, payload);
      if (!res.success) {
        toast('danger', res.message || 'Enregistrement impossible.');
        return;
      }
      toast('success', `Configuration enregistrée pour ${editingSite.name}.`);
      setEditingSite(null);
      await loadSettings();
    } catch (error) {
      toast('danger', error.message || 'Enregistrement impossible.');
    } finally {
      setSavingSite(false);
    }
  };

  return (
    <div className="adm-dash settings-page-v2">
      <AdminPageHeader
        badge={ADMIN_COPY.DASHBOARD.BADGE}
        title="Paramètres plateforme"
        description="Pharmacie centrale SMIE et modes de prise en charge par site — paramètres transverses."
        actions={
          <Link to={ADMIN_PATHS.SITES} className="co-create-btn co-btn-link-nav">
            <Icon name="home" size={14} />
            <span>Gérer les sites</span>
          </Link>
        }
      />

      <section className="co-shell adm-panel">
        {loading ? (
          <div className="st-settings-state">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 mb-0">Chargement des paramètres…</p>
          </div>
        ) : (
          <>
            <div className="st-settings-stats" aria-label="Vue d'ensemble">
              <article className="st-settings-stat st-settings-stat--central">
                <span className="st-settings-stat__label">Pharmacie centrale</span>
                <span className="st-settings-stat__value">
                  {stats.centralName || 'Non définie'}
                </span>
                <span className="st-settings-stat__hint">hub logistique SMIE</span>
              </article>
              <article className="st-settings-stat">
                <span className="st-settings-stat__label">Sites</span>
                <strong className="st-settings-stat__value">{stats.total}</strong>
                <span className="st-settings-stat__hint">répertoire complet</span>
              </article>
              <article className="st-settings-stat">
                <span className="st-settings-stat__label">Actifs</span>
                <strong className="st-settings-stat__value">{stats.active}</strong>
                <span className="st-settings-stat__hint">
                  {stats.total > 0 ? `${stats.active}/${stats.total}` : '—'}
                </span>
              </article>
              <article className="st-settings-stat">
                <span className="st-settings-stat__label">Pharmacie locale</span>
                <strong className="st-settings-stat__value">{stats.withPharmacy}</strong>
                <span className="st-settings-stat__hint">sites équipés</span>
              </article>
            </div>

            <div className="st-settings-body">
              <article className="st-central-card">
                <div className="st-central-card__head">
                  <div>
                    <h2 className="st-central-card__title">Pharmacie centrale</h2>
                    <p className="st-central-card__desc">
                      Point d&apos;ancrage des commandes fournisseurs, distributions inter-sites,
                      alertes stock et reporting pharmacie.
                    </p>
                  </div>
                  <span
                    className={`st-central-current ${
                      stats.centralName ? '' : 'st-central-current--empty'
                    }`}
                  >
                    {stats.centralName ? `Actuelle : ${stats.centralName}` : 'Aucune centrale'}
                  </span>
                </div>

                {canSetCentral ? (
                  <div className="st-central-form">
                    <div className="st-central-form__field">
                      <label className="st-central-form__label" htmlFor="st-central-select">
                        Nouveau site central
                      </label>
                      <select
                        id="st-central-select"
                        className="st-central-form__select"
                        value={selection}
                        onChange={(e) => setSelection(e.target.value)}
                      >
                        <option value="">Choisir un site…</option>
                        {sites
                          .filter((s) => s.is_active)
                          .map((s) => (
                            <option key={s.id} value={String(s.id)}>
                              {s.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="st-central-apply-btn"
                      onClick={openConfirm}
                      disabled={!selectedSite || centralUnchanged}
                    >
                      <Icon name="shield" size={14} />
                      <span>Définir comme centrale</span>
                    </button>
                  </div>
                ) : (
                  <p className="st-central-readonly mb-0">
                    Seul l&apos;administrateur système peut modifier la pharmacie centrale.
                  </p>
                )}
              </article>

              <article className="st-sites-panel">
                <div className="st-sites-panel__head">
                  <div>
                    <h2 className="st-sites-panel__title">Métier par site</h2>
                    <p className="st-sites-panel__desc">
                      Mode de prise en charge et pharmacie locale — sans modifier la fiche site
                      complète.
                    </p>
                  </div>
                  <label className="st-settings-search">
                    <Icon name="search" size={16} className="st-settings-search__icon" aria-hidden />
                    <input
                      type="search"
                      placeholder="Rechercher un site…"
                      value={siteSearch}
                      onChange={(e) => setSiteSearch(e.target.value)}
                      aria-label="Filtrer les sites"
                      autoComplete="off"
                    />
                  </label>
                </div>

                {sites.length === 0 ? (
                  <Alert variant="info" className="small mb-0">
                    Aucun site.{' '}
                    <Link to={ADMIN_PATHS.SITES}>Créer un site</Link> pour commencer.
                  </Alert>
                ) : filteredSites.length === 0 ? (
                  <p className="text-muted small mb-0">Aucun site ne correspond à la recherche.</p>
                ) : (
                  <div className="st-settings-table-wrap">
                    <table className="st-settings-table">
                      <thead>
                        <tr>
                          <th>Site</th>
                          <th>Configuration</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSites.map((site) => {
                          const isCentral = Number(site.is_central_pharmacy) === 1;
                          const hasPharmacy =
                            site.has_local_pharmacy ||
                            site.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY;
                          return (
                            <tr key={site.id} className={site.is_active ? '' : 'co-row--inactive'}>
                              <td>
                                <span className="st-settings-site-name">
                                  {site.name}
                                  {isCentral ? (
                                    <span className="st-central-pill">Centrale</span>
                                  ) : null}
                                  <span
                                    className={`co-status-dot ${
                                      site.is_active ? 'is-active' : 'is-inactive'
                                    }`}
                                    title={site.is_active ? 'Actif' : 'Inactif'}
                                    aria-hidden
                                  />
                                </span>
                              </td>
                              <td>
                                <div className="st-settings-config">
                                  <SiteCareModeBadge
                                    mode={site.site_care_mode}
                                    variant="compact"
                                    showIcon={false}
                                  />
                                  <span
                                    className={`st-pharmacy-line ${
                                      hasPharmacy ? 'st-pharmacy-line--yes' : 'st-pharmacy-line--no'
                                    }`}
                                  >
                                    Pharmacie locale : {hasPharmacy ? 'Oui' : 'Non'}
                                  </span>
                                </div>
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="st-configure-btn"
                                  onClick={() => openSiteEditor(site)}
                                >
                                  <Icon name="cog" size={14} />
                                  <span>Configurer</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            </div>
          </>
        )}
      </section>

      {showConfirm && (
        <div
          className="co-modal-overlay"
          role="presentation"
          onClick={() => !saving && setShowConfirm(false)}
        >
          <div
            className="co-modal st-settings-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="co-modal__header">
              <div>
                <span className="co-modal__badge co-modal__badge--warn">Action sensible</span>
                <h2>Changer la pharmacie centrale</h2>
              </div>
              <button
                type="button"
                className="co-modal__close"
                onClick={() => !saving && setShowConfirm(false)}
                aria-label="Fermer"
                disabled={saving}
              >
                <Icon name="times" size={18} />
              </button>
            </header>
            <div className="co-modal__body">
              <p className="small text-muted mb-2">
                Cette modification impacte les opérations centrales : commandes fournisseurs,
                distributions, alertes et reporting.
              </p>
              <p className="small mb-1">
                Nouveau site : <strong>{selectedSite?.name}</strong>
              </p>
              <p className="small mb-2">Saisissez exactement la phrase ci-dessous :</p>
              <code className="st-confirm-phrase">{expectedPhrase}</code>
              <input
                className="form-control mt-2"
                autoFocus
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Phrase de confirmation"
                aria-label="Phrase de confirmation"
              />
            </div>
            <footer className="co-modal__footer">
              <button
                type="button"
                className="co-btn-ghost"
                onClick={() => setShowConfirm(false)}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="st-central-apply-btn"
                onClick={applyChange}
                disabled={saving || confirmInput.trim() !== expectedPhrase}
              >
                {saving ? 'Mise à jour…' : 'Confirmer'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {editingSite && (
        <div
          className="co-modal-overlay"
          role="presentation"
          onClick={() => !savingSite && setEditingSite(null)}
        >
          <div
            className="co-modal st-site-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="co-modal__header">
              <div>
                <span className="co-modal__badge">Configuration métier</span>
                <h2>{editingSite.name}</h2>
              </div>
              <button
                type="button"
                className="co-modal__close"
                onClick={() => !savingSite && setEditingSite(null)}
                aria-label="Fermer"
                disabled={savingSite}
              >
                <Icon name="times" size={18} />
              </button>
            </header>
            <div className="co-modal__body">
              <span className="co-form-section-label">Mode de prise en charge</span>
              <div className="st-care-picker" role="radiogroup" aria-label="Mode métier">
                {CARE_MODE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={siteDraft.site_care_mode === option.value}
                    className={`st-care-picker__card st-care-picker__card--${option.variant} ${
                      siteDraft.site_care_mode === option.value ? 'is-selected' : ''
                    }`}
                    onClick={() =>
                      setSiteDraft((prev) => ({
                        ...prev,
                        site_care_mode: option.value,
                        has_local_pharmacy:
                          option.value === SITE_CARE_MODES.WITH_PHARMACY
                            ? true
                            : prev.has_local_pharmacy
                      }))
                    }
                  >
                    <span className="st-care-picker__title">{option.shortLabel}</span>
                    <p className="st-care-picker__desc">{option.description}</p>
                  </button>
                ))}
              </div>
              <div
                className={`st-form-switch-wrap mt-3 ${
                  pharmacyLocked ? 'is-locked' : ''
                }`}
              >
                <label htmlFor="st-settings-local-pharmacy">
                  <input
                    type="checkbox"
                    id="st-settings-local-pharmacy"
                    checked={pharmacyLocked ? true : Boolean(siteDraft.has_local_pharmacy)}
                    disabled={pharmacyLocked}
                    onChange={(e) =>
                      setSiteDraft((prev) => ({ ...prev, has_local_pharmacy: e.target.checked }))
                    }
                  />
                  Pharmacie locale disponible
                </label>
              </div>
            </div>
            <footer className="co-modal__footer">
              <button
                type="button"
                className="co-btn-ghost"
                onClick={() => setEditingSite(null)}
                disabled={savingSite}
              >
                Annuler
              </button>
              <button
                type="button"
                className="co-btn-primary"
                onClick={saveSiteCareSettings}
                disabled={savingSite}
              >
                {savingSite ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
